import type { SpeedFactor } from '@bringg/dashboard-sdk/dist/SpeedFactor/Service/SpeedFactor.service';

import { getNextDayOfTheWeek, getPrevDayOfTheWeek } from '../../utils/date';
import { INTERVAL_END_OF_DAY, INTERVAL_START_OF_DAY } from '../../../constants';
import { SpeedFactorGroupType, SpeedFactorUtcMidnightSplitGroup } from '../types';
import { createProxy } from './proxy-object';

enum IntervalPartType {
	Start = 'start',
	End = 'end'
}

export const groupByUtcMidnightSplit = (speedFactors: SpeedFactor[]) => {
	const helperHash: Record<string, SpeedFactor> = {};
	const groups: SpeedFactorUtcMidnightSplitGroup[] = [];
	const ungrouped: SpeedFactor[] = [];

	const processMidnightBoundingItem = (speedFactor: SpeedFactor) => {
		const { interval_start, interval_end, day_of_week, original_uuid } = speedFactor;
		let currentVariant: IntervalPartType;
		let matchingVariant: IntervalPartType;

		if (interval_start === INTERVAL_START_OF_DAY) {
			currentVariant = IntervalPartType.End;
			matchingVariant = IntervalPartType.Start;
		} else if (interval_end === INTERVAL_END_OF_DAY) {
			currentVariant = IntervalPartType.Start;
			matchingVariant = IntervalPartType.End;
		} else {
			ungrouped.push(speedFactor);
			return;
		}

		// UUID matching strategy
		const key = [currentVariant, day_of_week, original_uuid].join('_');
		const matchingKey = [
			matchingVariant,
			matchingVariant === IntervalPartType.Start
				? getPrevDayOfTheWeek(day_of_week)
				: getNextDayOfTheWeek(day_of_week),
			original_uuid
		].join('_');

		if (helperHash[key]) {
			ungrouped.push(helperHash[key]);
		}
		helperHash[key] = speedFactor;

		if (helperHash[matchingKey]) {
			const [start, end] =
				currentVariant === IntervalPartType.Start
					? [speedFactor, helperHash[matchingKey]]
					: [helperHash[matchingKey], speedFactor];

			groups.push(
				createProxy({
					groupType: SpeedFactorGroupType.UtcMidnightSplit,
					start,
					end
				})
			);
			delete helperHash[key];
			delete helperHash[matchingKey];
		}
	};

	speedFactors.forEach(speedFactor => {
		const { interval_start, interval_end } = speedFactor;
		if (
			(interval_start === INTERVAL_START_OF_DAY && interval_end === INTERVAL_END_OF_DAY) ||
			(interval_start !== INTERVAL_START_OF_DAY && interval_end !== INTERVAL_END_OF_DAY)
		) {
			ungrouped.push(speedFactor);
			return;
		}
		processMidnightBoundingItem(speedFactor);
	});

	// Push any unmatched items from helperHash
	ungrouped.push(...Object.values(helperHash));

	return {
		groups,
		ungrouped
	};
};
